import type { Project, SiteType } from "@fieldbook/common/types"
import { powerSyncDb } from "@/lib/powersync/db"
import type { PowerSyncProjectRow } from "@/lib/powersync/schema"

/**
 * Route-loader data access for PowerSync-backed entities.
 *
 * TanStack Router loaders run outside React, so they can't use the app's
 * hooks or contexts. These helpers wrap direct `powerSyncDb` reads with a
 * bounded wait for a row to appear (initial sync may still be in flight on
 * a cold start), so a loader can await an entity before its route renders.
 */

const PROJECT_BY_ID = "SELECT * FROM project WHERE id = ?"

/** Default budget for waiting on a row that may not have synced yet. */
export const DEFAULT_ROW_WAIT_MS = 10_000

/** Poll interval while waiting for a row. */
const ROW_POLL_INTERVAL_MS = 250

const WAIT_TIMED_OUT = Symbol("wait-timed-out")

async function beforeDeadline<T>(
  promise: Promise<T>,
  deadline: number,
): Promise<T | typeof WAIT_TIMED_OUT> {
  const remainingMs = Math.max(0, deadline - Date.now())
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<typeof WAIT_TIMED_OUT>((resolve) => {
    timeoutId = setTimeout(() => resolve(WAIT_TIMED_OUT), remainingMs)
  })

  try {
    return await Promise.race([promise, timeout])
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId)
  }
}

function parseJsonField<T>(raw: string | null | undefined, fallback: T): T {
  if (!raw) return fallback
  try {
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

/**
 * Shared row→domain mapping for the project table. Used by both
 * ProjectDataProvider (via its PowerSync query) and route loaders, so the
 * two data paths can't drift on how JSON columns are parsed.
 */
export const deserializeProject = (row: PowerSyncProjectRow): Project => {
  return {
    ...row,
    location_coordinate: row.location_coordinate ?? null,
    location_multipolygon: row.location_multipolygon ?? null,
    metadata: parseJsonField<Project["metadata"]>(row.metadata, null),
    site_types: parseJsonField<Project["site_types"]>(row.site_types, null),
    observation_custom_fields: parseJsonField<
      Project["observation_custom_fields"]
    >(row.observation_custom_fields, null),
  } as Project
}

/**
 * Wait for the first row matching `query` to appear in the local PowerSync
 * store, polling until `timeoutMs` elapses. Returns the row, or undefined
 * if it never appears. Database initialization is awaited explicitly so
 * initialization and query failures propagate instead of being reported as
 * a missing row. The readiness wait and every query share the same deadline.
 *
 * Reusable by any route loader that must gate rendering on a synced row.
 */
export const waitForRow = async <T extends object>(
  query: string,
  parameters: unknown[],
  timeoutMs: number = DEFAULT_ROW_WAIT_MS,
): Promise<T | undefined> => {
  const deadline = Date.now() + timeoutMs

  const ready = await beforeDeadline(powerSyncDb.waitForReady(), deadline)
  if (ready === WAIT_TIMED_OUT) return undefined

  for (;;) {
    const rows = await beforeDeadline(
      powerSyncDb.getAll<T>(query, parameters),
      deadline,
    )
    if (rows === WAIT_TIMED_OUT) return undefined
    if (rows[0]) return rows[0]
    if (Date.now() >= deadline) return undefined
    const delay = await beforeDeadline(
      new Promise<void>((resolve) => setTimeout(resolve, ROW_POLL_INTERVAL_MS)),
      deadline,
    )
    if (delay === WAIT_TIMED_OUT) return undefined
  }
}

/**
 * Load a project row (waiting for it to sync), deserialized to the domain
 * type. Returns undefined if the row never appears within the wait budget.
 */
export const loadProjectRow = async (
  projectId: string,
  timeoutMs?: number,
): Promise<Project | undefined> => {
  const row = await waitForRow<PowerSyncProjectRow>(
    PROJECT_BY_ID,
    [projectId],
    timeoutMs,
  )
  return row ? deserializeProject(row) : undefined
}

/**
 * Load a project and resolve its site type by name — the gate for routes
 * whose form schema depends on the site-type definition (e.g. the new-site
 * form). The site type is undefined when the project is missing or has no
 * site type with that name.
 */
export const loadProjectWithSiteType = async (
  projectId: string,
  siteTypeName: string,
  timeoutMs?: number,
): Promise<{ project?: Project; siteType?: SiteType }> => {
  const project = await loadProjectRow(projectId, timeoutMs)
  return {
    project,
    siteType: project?.site_types?.find((st) => st.name === siteTypeName),
  }
}
